import { keepPreviousData, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createF24, deleteF24, fetchF24Details, fetchF24List, fetchF24WithPages } from '~/api/bookkeeping/taxes';
import {
  fetchF24SddCutoff,
  fetchF24ValidBanks,
  getF24PdfDownloadUrl,
  hasNoUploadedF24,
  uploadF24Contabile,
} from '~/api/f24';
import {
  deleteF24OcrUpload,
  fetchF24OcrList,
  getF24OcrDownloadUrl,
  getF24OcrInfo,
  getF24OcrPage,
  requestF24OcrUploadUrl,
  saveF24OcrPage,
  submitF24OcrUpload,
  uploadF24OcrPdfToS3,
} from '~/api/f24Ocr';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import useExceptionLogger from '~/hooks/useExceptionLogger';
import { Company, CompanyQueryKeys } from '~/types/company';
import { F24ValidBanksQueryKeys } from '~/types/f24';
import { OcrPageResult } from '~/types/f24Ocr';
import { MandateQueryKeys } from '~/types/mandate';
import {
  F24,
  F24ActivationData,
  F24QueryFilters,
  F24QueryKeys,
  F24Source,
  F24UploadCreatePayload,
  F24UploadStatus,
} from '~/types/taxes';
import { downloadUrl } from '~/utils/download';
import { F24FormData } from '~/views/F24/pay/hooks/usePayF24Form';
import { isZeroAmount } from '~/views/F24/pay/utils/formHelpers';

type CreateF24Params = F24FormData & Partial<F24ActivationData>;

export const useCreateF24 = () => {
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation('f24', { keyPrefix: 'pay' });
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: async ({ conditionsAccepted, specificClausesAccepted, ...formData }: CreateF24Params) => {
      const zeroAmount = isZeroAmount(formData.totalAmount);
      if (!zeroAmount && !formData.executionDate) {
        throw new Error('Execution date is required');
      }
      if (!zeroAmount && !formData.accountId) {
        throw new Error('Account ID is required');
      }

      const commonFields = {
        ...(formData.accountId ? { account: { id: formData.accountId } } : {}),
        paymentDate: formData.executionDate!,
        name: formData.operationName, // form field is operationName, DB column is name
        source: F24Source.UPLOAD as const,
        conditionsAccepted,
        specificClausesAccepted,
      };

      let contabileFilename: string | undefined;
      if (formData.receipt) {
        contabileFilename = await uploadF24Contabile(formData.receipt, companyId);
      }

      if (!formData.f24UploadId) {
        throw new Error('F24 upload ID is required');
      }

      const payload: F24UploadCreatePayload = {
        ...commonFields,
        f24UploadId: formData.f24UploadId,
        pdfFilename: contabileFilename,
      };
      return createF24(companyId, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.all });
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.isFirstPayment(companyId) });
      queryClient.invalidateQueries({ queryKey: MandateQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.ocrList(companyId) });
      const cached = queryClient.getQueryData<{ company: Company }>(CompanyQueryKeys.company(companyId));
      if (!cached?.company.f24ConditionsAcceptedAt || !cached.company.f24SpecificClausesAcceptedAt) {
        queryClient.invalidateQueries({ queryKey: CompanyQueryKeys.allCompanies });
      }
    },
    onError(error) {
      snack.error(t('create_error'));
      captureException(error);
    },
  });
};

// WebSocket is the primary channel for OCR readiness; polling is just a safety
// net in case the event is missed. 60s keeps the UI roughly in sync without
// hammering the API — especially since F24 uploads are not frequent.
const OCR_LIST_POLL_INTERVAL_MS = 60_000;

export const useF24OcrListQuery = (enabled = true) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    enabled: enabled && Boolean(companyId),
    queryKey: F24QueryKeys.ocrList(companyId),
    queryFn: () => fetchF24OcrList(companyId),
    // Poll while at least one upload is still being processed; stop otherwise.
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasPending = data?.some((upload) => upload.status === ('WAITING_OCR' satisfies F24UploadStatus));
      return hasPending ? OCR_LIST_POLL_INTERVAL_MS : false;
    },
  });
};

export const useF24OcrItemQuery = (ocrJobId: string | null | undefined, enabled = true) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    enabled: enabled && Boolean(companyId) && Boolean(ocrJobId),
    queryKey: F24QueryKeys.ocrItem(companyId, ocrJobId ?? ''),
    queryFn: () => getF24OcrInfo(companyId, ocrJobId!),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const isWaitingOcr = data.status === ('WAITING_OCR' satisfies F24UploadStatus);
      const isReadyButPagesMissing =
        data.status === ('WAITING_REVIEW' satisfies F24UploadStatus) && data.pages.length === 0;
      return isWaitingOcr || isReadyButPagesMissing ? OCR_LIST_POLL_INTERVAL_MS : false;
    },
  });
};

export const useF24OcrPagesQuery = (ocrJobId: string | null | undefined, pages: number[], enabled = true) => {
  const companyId = useActiveCompanyId();
  const isEnabled = enabled && Boolean(companyId) && Boolean(ocrJobId) && pages.length > 0;

  return useQueries({
    queries: pages.map((page) => ({
      enabled: isEnabled,
      queryKey: F24QueryKeys.ocrPage(companyId, ocrJobId ?? '', page),
      queryFn: () => getF24OcrPage(companyId, ocrJobId!, page),
      staleTime: 0,
      gcTime: 0,
    })),
  });
};

export const useF24ListQuery = (enabled = true) => {
  const companyId = useActiveCompanyId();
  const filters: F24QueryFilters = {
    source: [F24Source.UPLOAD],
  };
  return useQuery({
    enabled,
    queryKey: F24QueryKeys.f24List(companyId, filters),
    queryFn: () => fetchF24List(companyId, filters),
  });
};

export const useIsFirstF24Payment = (enabled = true) => {
  const companyId = useActiveCompanyId();

  return useQuery({
    enabled: enabled && Boolean(companyId),
    queryKey: F24QueryKeys.isFirstPayment(companyId),
    queryFn: () => hasNoUploadedF24(companyId),
    staleTime: 30000,
  });
};

export const useF24ValidBanksQuery = () => {
  return useQuery({
    queryKey: F24ValidBanksQueryKeys.all,
    queryFn: fetchF24ValidBanks,
    staleTime: Infinity,
  });
};

export const useF24SddCutoffQuery = (paymentDate: string | null, enabled = true) => {
  return useQuery({
    enabled: enabled && Boolean(paymentDate),
    queryKey: F24QueryKeys.sddCutoff(paymentDate ?? ''),
    queryFn: () => fetchF24SddCutoff(paymentDate!),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
};

export const useF24WithPagesQuery = (f24Id: F24['id'] | null) => {
  return useQuery({
    enabled: Boolean(f24Id),
    queryKey: F24QueryKeys.withPages(f24Id!),
    queryFn: () => fetchF24WithPages(f24Id!),
  });
};

export const useF24DetailsQuery = (f24Id: F24['id'] | null) => {
  return useQuery({
    enabled: Boolean(f24Id),
    queryKey: F24QueryKeys.details(f24Id!),
    queryFn: () => fetchF24Details(f24Id!),
  });
};

export const useDeleteF24 = () => {
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation('f24', { keyPrefix: 'aside' });
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (f24Id: F24['id']) => deleteF24(f24Id),
    onSuccess: () => {
      snack.success(t('delete_success'));
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.all });
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.isFirstPayment(companyId) });
    },
    onError(error) {
      snack.error(t('delete_error'));
      captureException(error);
    },
  });
};

export const useDownloadF24Pdf = () => {
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation('f24', { keyPrefix: 'payments_table' });

  return useMutation({
    mutationFn: async (f24Id: F24['id']) => {
      const url = await getF24PdfDownloadUrl(f24Id);
      downloadUrl(url);
    },
    onError(error) {
      snack.error(t('download_error'));
      captureException(error);
    },
  });
};

export const useDownloadF24OcrPdfMutation = () => {
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation('f24', { keyPrefix: 'ocr_in_progress' });
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: async ({ ocrJobId, fileName }: { ocrJobId: string; fileName?: string }) => {
      const url = await getF24OcrDownloadUrl(companyId, ocrJobId);
      downloadUrl(url, fileName);
    },
    onError(error) {
      snack.error(t('download_error'));
      captureException(error);
    },
  });
};

interface SubmitF24OcrPdfVariables {
  file: File;
  name: string;
}

export const useSubmitF24OcrPdfMutation = () => {
  const { captureException } = useExceptionLogger();
  const companyId = useActiveCompanyId();

  return useMutation({
    // Presigned direct-to-S3 flow (replaces the deprecated multipart endpoint):
    // 1) ask BE for a presigned URL, 2) PUT the PDF straight to S3,
    // 3) submit → BE verifies the upload and starts the OCR.
    mutationFn: async ({ file, name }: SubmitF24OcrPdfVariables) => {
      const { ocr_job_id, upload_url } = await requestF24OcrUploadUrl(companyId, { name, fileName: file.name });
      await uploadF24OcrPdfToS3(upload_url, file);
      return submitF24OcrUpload(companyId, ocr_job_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.ocrList(companyId) });
    },
    onError: (error) => {
      captureException(error);
    },
  });
};

export const useDeleteF24OcrUploadMutation = () => {
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation('f24', { keyPrefix: 'ocr_in_progress' });
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: (ocrJobId: string) => deleteF24OcrUpload(companyId, ocrJobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.ocrList(companyId) });
    },
    onError: (error) => {
      snack.error(t('delete_error'));
      captureException(error);
    },
  });
};

interface SaveF24OcrPageVariables {
  ocrJobId: string;
  pdfPage: number;
  payload: OcrPageResult;
}

export const useSaveF24OcrPageMutation = () => {
  const { captureException } = useExceptionLogger();
  const { t } = useTranslation('f24', { keyPrefix: 'pay.review_form' });
  const companyId = useActiveCompanyId();

  return useMutation({
    mutationFn: ({ ocrJobId, pdfPage, payload }: SaveF24OcrPageVariables) =>
      saveF24OcrPage(companyId, ocrJobId, pdfPage, payload),
    onSuccess: (_data, { ocrJobId, pdfPage }) => {
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.ocrPage(companyId, ocrJobId, pdfPage) });
      queryClient.invalidateQueries({ queryKey: F24QueryKeys.ocrItem(companyId, ocrJobId) });
    },
    onError: (error) => {
      snack.error(t('errors.save_failed'));
      captureException(error);
    },
  });
};
